import { useEventSource } from "@vueuse/core";
import type { FetchError } from "ofetch";
import { ref, watch, onUnmounted } from "vue";
import type {
  TokenMetadata,
  TokenRefreshState,
  TokenRefreshResult,
  MercureTokenResponse,
} from "~/types/MercureToken";
import type { UserDetail } from "~/types/User";

export const useMercure = () => {
  const config = useRuntimeConfig();
  const nuxtApp = useNuxtApp();
  const { $eventBus } = nuxtApp;
  const sanctumUser = useSanctumUser();
  const sanctumConfig = useSanctumConfig();
  const { refreshIdentity } = useSanctumAuth();
  const getLastEventId = (key: string) => localStorage.getItem(key) || null;
  const setLastEventId = (key: string, value: string) =>
    localStorage.setItem(key, value);

  // Storage keys for persisting token metadata
  const TOKEN_STATE_KEY = "mercure_token_state";
  const REFRESH_STATE_KEY = "mercure_refresh_state";

  /**
   * Load token state from sessionStorage
   * Purpose: Restore token state after page refresh (OWASP: Secure storage)
   * Note: sessionStorage is cleared when browser tab closes
   */
  const loadTokenState = (): TokenMetadata => {
    try {
      const stored = sessionStorage.getItem(TOKEN_STATE_KEY);
      if (!stored) {
        return { jti: null, expiresAt: null, fingerprint: null };
      }

      const parsed = JSON.parse(stored);

      // Validate expiration - don't restore expired tokens
      if (parsed.expiresAt && new Date(parsed.expiresAt).getTime() < Date.now()) {
        console.log("Stored token expired, clearing");
        sessionStorage.removeItem(TOKEN_STATE_KEY);
        return { jti: null, expiresAt: null, fingerprint: null };
      }

      return parsed;
    }
    catch (error) {
      console.error("Failed to load token state:", error);
      return { jti: null, expiresAt: null, fingerprint: null };
    }
  };

  /**
   * Load refresh state from sessionStorage
   */
  const loadRefreshState = (): TokenRefreshState => {
    try {
      const stored = sessionStorage.getItem(REFRESH_STATE_KEY);
      if (!stored) {
        return {
          isRefreshing: false,
          lastRefreshAt: null,
          nextRefreshAt: null,
          refreshAttempts: 0,
          lastError: null,
        };
      }

      const parsed = JSON.parse(stored);
      // Reset isRefreshing flag on reload (it shouldn't persist)
      return { ...parsed, isRefreshing: false };
    }
    catch (error) {
      console.error("Failed to load refresh state:", error);
      return {
        isRefreshing: false,
        lastRefreshAt: null,
        nextRefreshAt: null,
        refreshAttempts: 0,
        lastError: null,
      };
    }
  };

  /**
   * Save token state to sessionStorage
   * Purpose: Persist across page refreshes (OWASP: Use sessionStorage, not localStorage)
   */
  const saveTokenState = (state: TokenMetadata) => {
    try {
      sessionStorage.setItem(TOKEN_STATE_KEY, JSON.stringify(state));
    }
    catch (error) {
      console.error("Failed to save token state:", error);
    }
  };

  /**
   * Save refresh state to sessionStorage
   */
  const saveRefreshState = (state: TokenRefreshState) => {
    try {
      // Don't persist the Error object, just the message
      const stateToPersist = {
        ...state,
        lastError: state.lastError ? { message: state.lastError.message } : null,
      };
      sessionStorage.setItem(REFRESH_STATE_KEY, JSON.stringify(stateToPersist));
    }
    catch (error) {
      console.error("Failed to save refresh state:", error);
    }
  };

  /**
   * Clear persisted token state
   */
  const clearTokenState = () => {
    sessionStorage.removeItem(TOKEN_STATE_KEY);
    sessionStorage.removeItem(REFRESH_STATE_KEY);
  };

  // OWASP: Token state management (with persistence)
  const tokenState = ref<TokenMetadata>(loadTokenState());

  const refreshState = ref<TokenRefreshState>(loadRefreshState());

  // Watch for changes and persist to sessionStorage
  watch(tokenState, (newState) => {
    saveTokenState(newState);
  }, { deep: true });

  watch(refreshState, (newState) => {
    saveRefreshState(newState);
  }, { deep: true });

  // Forgets the current token, in memory and in sessionStorage
  const resetTokenState = () => {
    tokenState.value = { jti: null, expiresAt: null, fingerprint: null };
    clearTokenState();
  };

  // OWASP: Refresh 3 minutes before expiry (configurable)
  const REFRESH_BUFFER = 180; // seconds
  const MAX_REFRESH_ATTEMPTS = 3;
  // The PDF service applies its rate limit before reading the token, so this answer can be retried
  const TOO_MANY_REQUESTS = 429;
  // Answers to the channel token request that mean the Backoffice session is gone, as nuxt-auth-sanctum reads them
  const SESSION_LOST_STATUSES = [401, 419];
  // Hub reconnection: one second, doubled after each failed attempt up to one minute
  const RECONNECT_BASE_DELAY = 1000; // ms
  const RECONNECT_MAX_DELAY = 60000; // ms

  type HubConnection = {
    isWaiting: () => boolean;
    reconnectNow: () => void;
    close: () => void;
  };

  let refreshTimer: ReturnType<typeof setTimeout> | null = null;
  let pendingReauthorization: Promise<boolean> | null = null;
  // Set on unmount or once the session is gone: nothing is scheduled or reconnected any more
  let isStopped = false;
  let isSessionExpired = false;
  const connections: HubConnection[] = [];

  const buildMercureUrl = (url: string, topics: string[], lastEventId: string | null = null) => {
    const mercureUrl = new URL(url);
    topics.forEach(topic => mercureUrl.searchParams.append("topic", topic));
    if (lastEventId) mercureUrl.searchParams.set("lastEventID", lastEventId);
    return mercureUrl.toString();
  };
  /**
   * Generate client-side fingerprint (OWASP: Additional client validation)
   * Purpose: Supplement server-side fingerprinting
   */
  const generateFingerprint = async (): Promise<string> => {
    const data = [
      navigator.userAgent,
      navigator.language,
      screen.colorDepth.toString(),
      `${screen.width}x${screen.height}`,
      new Date().getTimezoneOffset().toString(),
    ].join("###");

    // Check if crypto.subtle is available (requires secure context: HTTPS or localhost)
    if (typeof crypto !== "undefined" && crypto.subtle) {
      try {
        // Use Web Crypto API for SHA-256 hash
        const encoder = new TextEncoder();
        const dataBuffer = encoder.encode(data);
        const hashBuffer = await crypto.subtle.digest("SHA-256", dataBuffer);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
      }
      catch (error) {
        console.warn("crypto.subtle.digest failed, using fallback hash:", error);
      }
    }

    // Fallback: Simple hash function (not cryptographically secure, but sufficient for fingerprinting)
    // This is used in non-secure contexts (HTTP) where crypto.subtle is unavailable
    let hash = 0;
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }

    // Convert to hex and pad to simulate SHA-256 length
    const hashHex = Math.abs(hash).toString(16).padStart(8, "0");

    // Expand to 64 characters (SHA-256 length) for consistency
    return hashHex.repeat(8).substring(0, 64);
  };

  /**
   * Calculate exponential backoff delay
   * Purpose: OWASP - Prevent aggressive retry on failures
   */
  const calculateBackoff = (attempt: number): number => {
    return Math.min(1000 * Math.pow(2, attempt), 30000); // Max 30s
  };

  // Whether useSanctumClient failed because the server answered with one of these statuses
  const hasStatus = (error: unknown, statuses: number[]): boolean => {
    const status = (error as FetchError | null)?.response?.status;
    return status !== undefined && statuses.includes(status);
  };

  /**
   * Refresh Mercure token with OWASP security
   * Purpose: Token rotation, theft detection, fingerprint validation
   * Note: Reauthorizes first since refresh endpoint requires auth:sanctum
   */
  const refreshToken = async (): Promise<boolean> => {
    if (refreshState.value.isRefreshing) {
      console.log("Token refresh already in progress");
      return false; // Prevent concurrent refreshes
    }

    refreshState.value.isRefreshing = true;
    refreshState.value.refreshAttempts++;

    try {
      const apiClient = useSanctumClient();
      const user = await resolveCurrentUser();
      if (user === null) {
        return false;
      }

      // Get fresh Sanctum token
      const sanctumToken = await fetchChannelToken(user.id);
      if (sanctumToken === null) {
        return false;
      }

      const fingerprint = await generateFingerprint();

      // Send current JTI in request body (cookie might be expired)
      const response: TokenRefreshResult = await apiClient(
        `${config.public.pdfServiceUrl}/api/mercure/refresh-token`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "X-Requested-With": "XMLHttpRequest", // CSRF protection
            "X-Client-Fingerprint": fingerprint,
            "Authorization": `Bearer ${sanctumToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            jti: tokenState.value.jti, // Send JTI from sessionStorage
          }),
        },
      );
      if (!response.success) {
        // Same shape as the ofetch error for an error status, so the catch below handles both alike
        throw Object.assign(new Error(response.error || "Token refresh failed"), { data: response });
      }

      // Update token state
      tokenState.value = {
        jti: response.jti,
        expiresAt: new Date(response.expiresAt).getTime(),
        fingerprint,
      };

      // Schedule next refresh
      scheduleTokenRefresh();

      // Reset failure counter
      refreshState.value.refreshAttempts = 0;
      refreshState.value.lastError = null;
      refreshState.value.lastRefreshAt = Date.now();

      return true;
    }
    catch (error: any) {
      refreshState.value.lastError = error;

      console.error("Token refresh failed:", error);

      const canRetry = hasStatus(error, [TOO_MANY_REQUESTS])
        && refreshState.value.refreshAttempts < MAX_REFRESH_ATTEMPTS;

      // OWASP: Token theft detected
      if (error?.data?.theftDetected) {
        handleTokenTheft(error.data);
      }
      else if (canRetry) {
        // Send the same token again later. Other failures are not retried: the server may
        // already have consumed the token, and a second use would be reported as a reuse that
        // revokes every token of the user.
        if (refreshTimer) {
          clearTimeout(refreshTimer);
          refreshTimer = null;
        }
        if (!isStopped) {
          const retryDelay = calculateBackoff(refreshState.value.refreshAttempts);
          refreshState.value.nextRefreshAt = Date.now() + retryDelay;
          refreshTimer = setTimeout(() => {
            refreshToken();
          }, retryDelay);
        }

        return false;
      }
    }
    finally {
      refreshState.value.isRefreshing = false;
    }

    // This token chain cannot go on, and its token may already be consumed: never send it again
    resetTokenState();
    if (isStopped) {
      return false;
    }

    // A new authorization only needs the Backoffice session, so the user does not have to log in again
    return reauthorize();
  };

  /**
   * Handle token theft detection (OWASP Security Event)
   * Purpose: Respond to token reuse or fingerprint mismatch, for which the server revoked the token
   */
  const handleTokenTheft = (response: TokenRefreshResult) => {
    // Clear all local and persisted state
    resetTokenState();

    // Clear refresh timer
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }

    // Log security event
    console.error("[SECURITY] Token theft detected:", response);
  };

  /**
   * The Backoffice session is gone, so no Mercure token can be obtained any more
   * Purpose: Stop every retry and send the user to the login route of nuxt-auth-sanctum
   */
  const handleSessionExpired = async () => {
    if (isSessionExpired) {
      return;
    }
    isSessionExpired = true;
    stopAll();

    $eventBus.emit("toast", {
      severity: "error",
      summary: "Session expirée",
      detail: "Votre session a expiré. Veuillez vous reconnecter.",
      life: 10000,
    });

    // Same steps as nuxt-auth-sanctum after an unauthenticated answer. Without an identity,
    // its guest middleware lets the login page open instead of redirecting back.
    sanctumUser.value = null;
    const loginRoute = sanctumConfig.redirect.onAuthOnly;
    if (sanctumConfig.redirectIfUnauthenticated && loginRoute) {
      await nuxtApp.callHook("sanctum:redirect", loginRoute);
      await nuxtApp.runWithContext(async () => await navigateTo(loginRoute));
    }
  };

  /**
   * Schedule token refresh before expiry
   * Purpose: OWASP - Proactive token refresh
   */
  const scheduleTokenRefresh = () => {
    if (isStopped) {
      return;
    }

    if (!tokenState.value.expiresAt) {
      console.warn("Cannot schedule refresh: no expiration time");
      return;
    }

    const now = Date.now();
    const expiresAt = tokenState.value.expiresAt;
    const tokenLifetimeMs = expiresAt - now;

    // IMPORTANT: Ensure refresh buffer doesn't exceed token lifetime
    // If token expires in 2 minutes (120s), don't try to refresh after 3 minutes (180s)!
    // Use at most 50% of token lifetime for refresh buffer, with minimum of 30 seconds
    const effectiveBufferMs = Math.min(
      REFRESH_BUFFER * 1000, // Original buffer (180s = 3min)
      Math.max(tokenLifetimeMs * 0.5, 30000), // 50% of lifetime, minimum 30s
    );

    const refreshAt = expiresAt - effectiveBufferMs;
    const delay = Math.max(1000, refreshAt - now); // Minimum 1 second delay to prevent tight loops

    // Don't schedule if token is already expired or about to expire (< 1 second)
    if (tokenLifetimeMs <= 1000) {
      console.warn("Token already expired or about to expire, not scheduling refresh");
      return;
    }

    refreshState.value.nextRefreshAt = refreshAt;

    // Clear existing timeout
    if (refreshTimer) {
      clearTimeout(refreshTimer);
    }

    // Schedule refresh
    refreshTimer = setTimeout(() => {
      console.log("Auto-refreshing token (scheduled)");
      refreshToken();
    }, delay);
  };

  /**
   * Check if token needs reauthorization
   * Returns true if token is missing or expired
   */
  const needsReauthorization = (): boolean => {
    if (!tokenState.value.jti || !tokenState.value.expiresAt) {
      return true;
    }
    return tokenState.value.expiresAt <= Date.now();
  };

  /**
   * Reauthorize and get a fresh token
   * Use this when token is expired or missing
   * Concurrent callers share the attempt in progress, so only one reauthorization runs at a time
   */
  const reauthorize = (): Promise<boolean> => {
    if (!pendingReauthorization) {
      pendingReauthorization = authorizeCurrentUser().finally(() => {
        pendingReauthorization = null;
      });
    }
    return pendingReauthorization;
  };

  const authorizeCurrentUser = async (): Promise<boolean> => {
    if (isSessionExpired) {
      return false;
    }

    try {
      const user = await resolveCurrentUser();
      if (user === null) {
        return false;
      }

      const authPath = `${config.public.pdfServiceUrl}/api/mercure/authorize`;
      const response = await authorize(authPath, user.id);

      return response?.success ?? false;
    }
    catch (error) {
      console.error("Reauthorization failed:", error);
      return false;
    }
  };

  /**
   * Initialize token refresh on composable creation
   * Purpose: Restore refresh timer after page reload
   * Note: Does NOT automatically reauthorize - consuming components should handle that
   */
  const initializeTokenRefresh = () => {
    // If we have a valid token state from sessionStorage, reschedule refresh
    if (tokenState.value.jti && tokenState.value.expiresAt) {
      const now = Date.now();
      const expiresAt = tokenState.value.expiresAt;

      // Check if token is still valid
      if (expiresAt > now) {
        console.log("Restoring token refresh after page reload");
        scheduleTokenRefresh();
      }
      else {
        // Token expired during page reload - clear it
        // Consuming components should check needsReauthorization() and call reauthorize()
        console.log("Token expired during reload, clearing state");
        resetTokenState();
      }
    }
  };

  // Initialize on composable creation
  initializeTokenRefresh();

  /**
   * A hidden tab can miss its scheduled refresh (throttled timers, sleep): catch up once visible again
   */
  const onVisibilityChange = () => {
    if (document.visibilityState !== "visible") {
      return;
    }

    // A connection waiting for its next attempt retries now, and re-authorizes on the way
    const waitingConnections = connections.filter(connection => connection.isWaiting());
    if (waitingConnections.length > 0) {
      waitingConnections.forEach(connection => connection.reconnectNow());
      return;
    }

    const { nextRefreshAt } = refreshState.value;
    if (needsReauthorization()) {
      reauthorize();
    }
    else if (nextRefreshAt !== null && nextRefreshAt <= Date.now()) {
      refreshToken();
    }
  };

  if (import.meta.client) {
    document.addEventListener("visibilitychange", onVisibilityChange);
  }

  // Stops every timer, listener and hub connection this composable owns
  const stopAll = () => {
    isStopped = true;
    if (refreshTimer) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
    connections.splice(0).forEach(connection => connection.close());
    if (import.meta.client) {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    }
  };

  // Cleanup on unmount
  onUnmounted(stopAll);

  /**
   * Resolves the user the channel token is requested for
   * The user store only keeps a copy of the nuxt-auth-sanctum identity, so an empty store does not mean
   * that the Backoffice session is gone: the identity is then read again from the Backoffice.
   * Resolves to null once the Backoffice session is gone.
   */
  const resolveCurrentUser = async (): Promise<Pick<UserDetail, "id"> | null> => {
    const userStore = useUserStore();
    const storedUser = toValue(await userStore.getUser());
    if (storedUser?.id != null) {
      return storedUser;
    }

    try {
      await refreshIdentity();
    }
    catch (error) {
      if (!hasStatus(error, SESSION_LOST_STATUSES)) {
        throw error;
      }
      await handleSessionExpired();
      return null;
    }

    // getUser() alone would keep a stored user that has no id
    await userStore.loadUser();
    const user = toValue(await userStore.getUser());
    if (user?.id == null) {
      // The session is alive, but it has no user in this tenant to issue a channel token for
      throw new Error("The Backoffice identity has no user id");
    }
    return user;
  };

  /**
   * Channel token issued by the Backoffice, which the PDF service accepts as a bearer
   * Resolves to null once the Backoffice session is gone
   */
  const fetchChannelToken = async (userId: number): Promise<string | null> => {
    try {
      return await useSanctumClient()(`/api/user/${userId}/channel/token`);
    }
    catch (error) {
      if (!hasStatus(error, SESSION_LOST_STATUSES)) {
        throw error;
      }
      await handleSessionExpired();
      return null;
    }
  };

  /**
   * Initial Mercure authorization with OWASP security
   * Purpose: Get initial token with fingerprinting and schedule refresh
   */
  const authorize = async (path: string, userId: number): Promise<MercureTokenResponse | null> => {
    const apiClient = useSanctumClient();
    try {
      // Get Sanctum token for PDF service
      const sanctumToken = await fetchChannelToken(userId);
      if (sanctumToken === null) {
        return null;
      }

      // Generate client fingerprint
      const fingerprint = await generateFingerprint();

      // Call authorization endpoint (returns token metadata + sets cookie)
      // An error answer here comes from the PDF service, not from the Backoffice session: it is read
      // as a failed authorization below instead of going through the logout nuxt-auth-sanctum
      // applies to any 401
      const response: MercureTokenResponse = await apiClient(path, {
        credentials: "include",
        ignoreResponseError: true,
        headers: {
          "Accept": "application/json",
          "Authorization": `Bearer ${sanctumToken}`,
          "X-Client-Fingerprint": fingerprint,
        },
      });

      if (response?.success) {
        // Store token metadata
        tokenState.value = {
          jti: response.jti,
          expiresAt: new Date(response.expiresAt).getTime(),
          fingerprint,
        };

        // A new token chain starts with no failed refresh
        refreshState.value.refreshAttempts = 0;
        refreshState.value.lastError = null;

        // Schedule automatic refresh
        scheduleTokenRefresh();

        return response;
      }

      throw new Error("Authorization failed");
    }
    catch (err) {
      // Failures stay silent: the hub connection retries the authorization on its own
      console.error("Failed to authorize Mercure:", err);
      return null;
    }
  };

  const createEventSource = (
    sseUrl,
    topicUrls,
    topicsToWatch,
    storageKey,
    featureDescription,
  ) => {
    if (isStopped) {
      return null;
    }

    const subscriptionUrl = ref(buildMercureUrl(sseUrl, topicUrls, getLastEventId(storageKey)));

    const source = useEventSource(
      subscriptionUrl,
      topicsToWatch,
      {
        immediate: true,
        // Reconnection is driven below: it has to re-authorize first, which the built-in retry cannot do
        autoConnect: false,
        autoReconnect: false,
        withCredentials: true,
      },
    );

    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let reconnectDelay = RECONNECT_BASE_DELAY;

    const reconnect = async () => {
      reconnectTimer = null;
      // The hub refused the connection, most often because its cookie expired: get a new one first
      const isAuthorized = await reauthorize();
      if (isStopped) {
        return;
      }
      if (!isAuthorized) {
        scheduleReconnect();
        return;
      }
      // A new EventSource does not send Last-Event-ID: resume after the last event already received
      subscriptionUrl.value = buildMercureUrl(sseUrl, topicUrls, getLastEventId(storageKey));
      source.open();
    };

    const scheduleReconnect = () => {
      if (isStopped || reconnectTimer) {
        return;
      }
      reconnectTimer = setTimeout(reconnect, reconnectDelay);
      reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_DELAY);
    };

    const stopWatchers = [
      // Setup event ID tracking
      watch([source.lastEventId], ([newEventId]) => {
        if (newEventId) {
          setLastEventId(storageKey, newEventId);
        }
      }),

      // Setup status tracking: an open connection resets the reconnection delay
      watch(source.status, (newStatus) => {
        if (newStatus === "OPEN") {
          reconnectDelay = RECONNECT_BASE_DELAY;
        }
        else if (newStatus === "CLOSED" && source.error.value) {
          console.error(
            `${featureDescription} connection closed with error:`,
            source.error.value,
          );
        }
      }),

      // After a dropped stream the browser reconnects on its own; it gives up (CLOSED) when the hub
      // answers with an error status, such as a 401 for a missing or expired cookie
      watch(source.error, (error) => {
        if (error && source.eventSource.value?.readyState === EventSource.CLOSED) {
          scheduleReconnect();
        }
      }),
    ];

    connections.push({
      isWaiting: () => reconnectTimer !== null,
      reconnectNow: () => {
        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
          reconnect();
        }
      },
      close: () => {
        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
          reconnectTimer = null;
        }
        stopWatchers.forEach(stopWatcher => stopWatcher());
        source.close();
      },
    });

    return source;
  };

  return {
    // Original functions
    buildMercureUrl,
    authorize,
    createEventSource,

    // OWASP token management functions
    generateFingerprint,
    refreshToken,
    reauthorize, // Get fresh token when expired/missing
    needsReauthorization, // Check if token needs refresh
    scheduleTokenRefresh,
    clearTokenState, // For manual logout

    // Token state (read-only)
    tokenState: readonly(tokenState),
    refreshState: readonly(refreshState),
  };
};
